import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import styled, { css, keyframes } from 'styled-components';

import { ElementState } from '../../renderer/ElementState';
import { TranscriptElement } from '../../types.ts/transcript';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { mapToElementState } from '@src/videoTranscriptionProcessor/utils';
import VideoCreatorStore from '@src/stores/VideoCreatorStore';
import chunk from 'lodash/chunk';

type ElementProps = {
  value: string | null;
  state: TranscriptElement['state'];
  index: number;
  // isHighlighted: boolean;
  isPhotoHighlight: boolean;
  isLastPhotoHighlight: boolean;
  isPhotoAssigned: boolean;
};

const Element = React.memo(
  (props: ElementProps) => {
    const { value, index } = props;
    return (
      <WordLight key={`element-${index}`} data-index={index} className="">
        {value}
      </WordLight>
    );
  },
  (prevProps, nextProps) => prevProps.value === nextProps.value, // update only if content changes
);

type Props = {
  cursor: { from: number; to: number };
  transcriptElements: TranscriptElement[] | undefined;
  scrollRef: React.RefObject<HTMLDivElement>;
};

export const TranscriptionText: React.FC<Props> = observer(
  ({ cursor, transcriptElements, scrollRef }) => {
    const videoCreator = useVideoCreatorStore();

    // UPDATE REASON 1: cursor moves
    const prevCursor = useRef<{ from: number; to: number } | null>(null);
    useEffect(() => {
      if (
        cursor.from === prevCursor.current?.from &&
        cursor.to === prevCursor.current?.to
      ) {
        return;
      }

      adjustDOMClassesToCursor(
        cursor,
        prevCursor.current,
        scrollRef,
        videoCreator.isPlaying,
      );

      prevCursor.current = cursor;
    }, [cursor]);

    // UPDATE REASON 2: transcript elements change
    const prevClasses = useRef<string[]>([]);
    useEffect(() => {
      (async () => {
        if (!transcriptElements?.length) {
          return;
        }

        const newClasses = transcriptElements.map((el, index) =>
          transcriptElementToWordClassName(el, index, videoCreator),
        );

        await adjustDOMClassesToElementStateUpdates(
          newClasses,
          prevClasses.current,
          scrollRef,
        );

        prevClasses.current = newClasses;
      })();
    }, [transcriptElements]);

    return (
      <Wrapper>
        <TranscriptionTextInner transcriptElements={transcriptElements} />
      </Wrapper>
    );
  },
);

const TranscriptionTextInner: React.FC<Pick<Props, 'transcriptElements'>> =
  React.memo(({ transcriptElements }) => {
    return (
      <>
        {transcriptElements?.map((el, index) => {
          if (el.state === 'removed' || el.state === 'cut') {
            return null;
          }
          return (
            <Element
              key={index}
              value={el.value}
              state={el.state}
              index={index}
              isPhotoHighlight={!!el.photo_highlight_id}
              isLastPhotoHighlight={!!el.last_photo_highlight}
              isPhotoAssigned={false}
            />
          );
        })}
      </>
    );
  });

const Wrapper = styled.div`
  padding-left: 2px; /* for cursor when it starts on the newline */
`;

const cursorAnimation = keyframes`
  100% {
    opacity: 0;
  }
`;

const WordLight = styled.span`
  color: #bdbdbd;
  background-color: none;
  position: relative;
  cursor: pointer;
  text-decoration: none;
  opacity: 1;

  &.whitespace {
    white-space: pre-wrap;
  }
  &.highlighted {
    color: black;
    background-color: #dfb615;
  }
  &.highlighted-by-photo {
    text-decoration: underline;
    color: #45d483;
  }
  &.state-muted {
    color: #f69b12;
  }
  &.state-removed {
    cursor: default;
    text-decoration: line-through;
    color: indianred;
  }
  &.state-cut {
    cursor: default;
    text-decoration: line-through;
    opacity: 0.6;
    color: #659960;
  }
  &.state-added-with-diff {
    color: #7bb975;
  }

  &.with-cursor {
    display: inline-block;
  }
  &.with-cursor::before {
    content: '';
    position: absolute;
    animation: ${cursorAnimation} steps(2) 1.5s infinite;
    height: 26px;
    width: 3px;
    background-color: #2a85ff;
    display: inline-block;
    border-radius: 1px;
    left: -2px;
    bottom: 0px;
    will-change: opacity;
  }

  &.with-photo::after {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    margin-left: 5px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
  }
  &.with-photo--assigned::after {
    content: url('/svgs/TimelinePhotoIcon_active.svg');
    background:
      linear-gradient(0deg, #45d483 0%, #45d483 100%),
      #45d483 50% / cover no-repeat;
  }
  &.with-photo--unassigned::after {
    content: url('/svgs/TimelinePhotoIcon_inactive.svg');
    background:
      linear-gradient(0deg, #4f4f4f 0%, #4f4f4f 100%),
      lightgray 50% / cover no-repeat;
  }

  &:hover {
    color: lightgreen !important;
  }
`;

const colorByState = (
  state: string,
  isHighlighted: boolean,
  isPhotoHighlight: boolean,
  diff?: boolean,
) => {
  if (isHighlighted) return 'black';
  if (state === 'muted') return '#F69B12';
  if (isPhotoHighlight) return '#45d483';
  return (
    {
      ...(diff && { added: '#7bb975' }),
      removed: 'indianred',
      cut: '#659960', // '#7bb975',
      muted: '#F69B12',
    }[state] || '#bdbdbd'
  );
};

// Define the props interface for Word component
interface WordProps {
  isHighlighted?: boolean;
  isCut?: boolean;
  isText?: boolean;
  state?: 'added' | 'removed' | 'muted' | 'replaced' | 'cut';
  isPhotoHighlight?: boolean;
  diff?: boolean;
  isWhitespace?: boolean;
  // cursor: 'after' | 'before' | 'none';
  children?: React.ReactNode;
}

export const Word = styled.span.attrs(
  (props: {
    isHighlighted?: boolean;
    isCut?: boolean;
    isText?: boolean;
    state?: 'added' | 'removed' | 'muted' | 'replaced' | 'cut';
    isPhotoHighlight?: boolean;
    diff?: boolean;
    isWhitespace?: boolean;
    // cursor: 'after' | 'before' | 'none';
  }) => props,
)`
  color: ${(props) =>
    colorByState(
      props.state || '',
      props.isHighlighted || false,
      props.isPhotoHighlight || false,
      props.diff || false,
    )};
  background-color: ${(props) => (props.isHighlighted ? '#dfb615' : 'none')};
  cursor: ${(props) =>
    props.state !== 'removed' && props.state !== 'cut' ? 'pointer' : 'default'};
  text-decoration: ${(props) =>
    props.state === 'removed' || props.state === 'cut'
      ? 'line-through'
      : props.isPhotoHighlight
        ? 'underline'
        : 'none'};
  position: relative;
  opacity: ${(props) => (props.isCut ? 0.6 : 1)};

  ${(props) => props.isWhitespace && `white-space: pre-wrap;`}

  &:hover {
    color: lightgreen;
  }
`;


// ${(props) => props.cursor === 'after' && css`
//     &::after {
//       content: '';
//       position: absolute;
//       animation: ${cursorAnimation} steps(2) 1.5s infinite;
//       height: 26px;
//       width: 3px;
//       background-color: #2a85ff;
//       display: inline-block;
//       border-radius: 1px;
//       right: -2px;
//       bottom: -4px;
//     }
//   `}

export const Cursor = styled.span
  .attrs({
    'data-testid': 'transcript-cursor',
  })
  .withConfig({
    displayName: 'TranscriptCursor',
  })`
  animation: ${cursorAnimation} steps(2) 1.5s infinite;
  height: 26px;
  margin-bottom: -8px;
  width: 3px;
  background-color: #2a85ff;
  display: inline-block;
  border-radius: 1px;
`;

export const HighlightImageWrapper = styled.div.withConfig({
  displayName: 'TranscriptHighlightWrapper',
})`
  position: relative;
  display: inline;
`;

export const HighlightImage = styled.div
  .withConfig({
    displayName: 'TranscriptHighlight',
  })
  .attrs((props: { isAssigned: boolean }) => props)`
  background: ${(props) =>
    props.isAssigned
      ? 'linear-gradient(0deg, #45d483 0%, #45d483 100%), #45d483'
      : 'linear-gradient(0deg, #4f4f4f 0%, #4f4f4f 100%), lightgray'}
    50% / cover no-repeat;
  color: #4f4f4f;
  border-radius: 50%;
  padding: 6px;
  margin-left: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

// Utilities for a CSS class-based approach of bypassing react re-renders

function elementPropsToWordClassName(props: ElementProps): string {
  const {
    value,
    state,
    isPhotoHighlight,
    isLastPhotoHighlight,
    isPhotoAssigned,
  } = props;
  return [
    state && `state-${state}`,
    isPhotoHighlight && 'highlighted-by-photo',
    isLastPhotoHighlight && 'with-photo',
    isLastPhotoHighlight &&
      (isPhotoAssigned ? 'with-photo--assigned' : 'with-photo--unassigned'),
    typeof value === 'string' && value.trim() === '' && 'whitespace',
  ]
    .filter(Boolean)
    .join(' ');
}

function transcriptElementToWordClassName(
  el: TranscriptElement,
  index: number,
  videoCreator: VideoCreatorStore,
): string {
  let hasPhotoHighlight = false;
  const source = videoCreator.rootStore.videoSourceStore?.source;
  if (el.last_photo_highlight && source?.elements) {
    const element: ElementState['source'] | undefined = source.elements.find(
      (e: ElementState['source']) => e.id === el.photo_highlight_id,
    );
    const imageElement = element
      ? videoCreator.getImageElement(mapToElementState(element) as ElementState)
      : null;
    hasPhotoHighlight = !!imageElement?.source?.source;
  }

  const props: ElementProps = {
    value: el.value,
    state: el.state,
    index: index,
    isPhotoHighlight: !!el.photo_highlight_id,
    isLastPhotoHighlight: !!el.last_photo_highlight,
    isPhotoAssigned: hasPhotoHighlight,
  };

  return elementPropsToWordClassName(props);
}

function calculateClassesDiff(
  newClasses: string[],
  prevClasses: string[],
): { added: string[]; removed: string[] }[] {
  return newClasses.map((newClass, index) => {
    if (newClass.includes('state-removed') || newClass.includes('state-cut')) {
      return { added: [], removed: [] };
    }
    const prevClass = prevClasses[index] || '';
    const added = newClass.split(' ').filter((c) => !prevClass.includes(c));
    const removed = prevClass.split(' ').filter((c) => !newClass.includes(c));
    return { added, removed };
  });
}

function adjustDOMClassesToElementStateUpdates(
  newClasses: string[],
  prevClasses: string[],
  scrollRef: React.RefObject<HTMLDivElement>,
) {
  return new Promise<void>((resolve) => {
    const prevClassesChunked = chunk(prevClasses, 2500);
    const newClassesChunked = chunk(newClasses, 2500);

    for (let i = 0; i < newClassesChunked.length; i++) {
      setTimeout(() => {
        const classesDiff = calculateClassesDiff(
          newClassesChunked[i],
          prevClassesChunked[i] ?? [],
        );
        const updates = [];
        for (let j = 0; j < classesDiff.length; j++) {
          const { added, removed } = classesDiff[j];
          if (added.length === 0 && removed.length === 0) {
            continue;
          }
          updates.push({
            added,
            removed,
            domElement: scrollRef.current?.querySelector(
              `[data-index='${j + i * newClassesChunked[i].length}']`,
            ),
          });
        }

        for (const { added, removed, domElement } of updates) {
          if (!domElement) {
            continue;
          }
          if (added.length > 0) {
            domElement.classList.add(...added);
          }
          if (removed.length > 0) {
            domElement.classList.remove(...removed);
          }
        }

        if (i === newClassesChunked.length - 1) {
          resolve();
        }
      }, i * 250);
    }
  });
}

function adjustDOMClassesToCursor(
  newCursor: {
    from: number;
    to: number;
  },
  prevCursor: { from: number; to: number } | null,
  scrollRef: React.RefObject<HTMLDivElement>,
  isPlaying: boolean,
) {
  if (!scrollRef.current) {
    return;
  }

  // split selecting and modifying into 2 steps to prevent layout thrashing

  const oldDOMList = [];
  const newDOMList = [];

  if (prevCursor) {
    for (let i = prevCursor.from; i < prevCursor.to; i++) {
      oldDOMList.push(scrollRef.current.querySelector(`[data-index='${i}']`));
    }
  }
  for (let i = newCursor.from; i < newCursor.to; i++) {
    newDOMList.push(scrollRef.current.querySelector(`[data-index='${i}']`));
  }

  for (const el of oldDOMList) {
    if (!el) {
      continue;
    }
    el.classList.remove('highlighted', 'with-cursor');
  }
  for (let i = newCursor.from, j = 0; i < newCursor.to; i++, j++) {
    const el = newDOMList[j];
    if (!el) {
      continue;
    }
    if (isPlaying) {
      el.classList.add('highlighted');
    } else if (i === newCursor.from) {
      el.classList.add('with-cursor');
    }
  }
}
